/**
 * My Calendar — one chronology of legislative events across every workspace
 * the user can reach (my_events=true collects tracked bills' hearings and
 * floor readings). The agenda scrolls internally; day headers stick.
 *
 * The panel owns a 14-day window anchored to a selectable date: the calendar
 * icon pops a month picker (MiniMonthCalendar), the day strip scrolls
 * sideways across the window, and the chevrons shift it a week at a time.
 */
import React, { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { addDays, format, isToday, parseISO } from 'date-fns';
import { Bookmark, ChevronLeft, ChevronRight } from 'lucide-react';
import DatePickerPopover from '../../calendar/DatePickerPopover';
import { SpinnerIcon } from '../../icons';
import type { CalendarEvent } from '../../../types/calendar';
import { formatAgendaDay, formatEventTime, toDateOnly, workspaceDotClass } from './homeFormat';
import { PRO_HOME_CALENDAR_DAYS, useMyCalendarEvents } from './useProHomeData';
import type { HomeMode } from './useHomeMode';
import { deriveDefaultScope, getStoredCalendarScope, setStoredCalendarScope, type CalendarScope } from '../../../utils/calendarPrefs';
import EventSourceBadge, { EventAttribution } from '../../calendar/EventSourceBadge';
import EventWorkspaceSelectionModal from '../../EventWorkspaceSelectionModal';
import EventDetailsModal from '../../calendar/EventDetailsModal';

/* Dashboard calendar views (CALENDAR_SEPARATION_PRD §5.3): Session =
   Legislative + Workspace (+ optional Public via the toggle); Elections =
   Election Cycle + Public + Workspace, with NO public toggle — political
   events are part of the elections view by definition (resolved O1/O2).
   The Session/Elections choice is the PAGE's mode toggle — the card inherits
   it (Angie, 2026-08-22). My/All is the SHARED calendar scope (§5.7): one
   persisted choice carried between this panel and both calendar pages, with
   a smart default (nothing of yours in window → All Events) until the user
   clicks. Public remains a card choice. */
const PUBLIC_KEY = 'cc-home-calendar-public';

function loadPref(key: string, fallback: string): string {
  try { return window.localStorage.getItem(key) ?? fallback; } catch { return fallback; }
}
function savePref(key: string, value: string): void {
  try { window.localStorage.setItem(key, value); } catch { /* private mode */ }
}

interface TrackedBillLike {
  is_tracked?: boolean;
  tracked_by_workspaces?: string[];
}

function workspaceTags(event: CalendarEvent): string[] {
  // The range endpoint attaches bills[] with tracked_by_workspaces when it can
  // resolve them; collect the distinct workspace names on the event.
  const bills = (event as CalendarEvent & { bills?: TrackedBillLike[] }).bills ?? [];
  const names = new Set<string>();
  for (const bill of bills) {
    for (const name of bill.tracked_by_workspaces ?? []) names.add(name);
  }
  return Array.from(names).slice(0, 2);
}

const MyCalendarPanel: React.FC<{ mode: HomeMode }> = ({ mode }) => {
  const todayKey = format(new Date(), 'yyyy-MM-dd');
  const [anchor, setAnchor] = useState(todayKey);
  // Shared scope (§5.7): explicit choice > smart default derived from the
  // All-Events window. The derived value is never persisted — only clicks are.
  const [scopeChoice, setScopeChoice] = useState<CalendarScope | null>(() => getStoredCalendarScope());
  const [derivedScope, setDerivedScope] = useState<CalendarScope | null>(null);
  const myOnly = (scopeChoice ?? derivedScope) === 'my';
  // Off by default (CALENDAR_SEPARATION_PRD §5.3): with the legislature
  // adjourned and no milestones in window, a default-on Public toggle made
  // Session and Elections views render the identical political-events list —
  // the exact conflation the separation exists to end (reported 2026-08-26).
  // An explicit stored choice still wins.
  const [showPublic, setShowPublicState] = useState<boolean>(() => loadPref(PUBLIC_KEY, 'off') === 'on');
  const setMyOnly = (next: boolean) => {
    const scope: CalendarScope = next ? 'my' : 'all';
    setScopeChoice(scope);
    setStoredCalendarScope(scope);
  };
  const setShowPublic = (next: boolean) => { setShowPublicState(next); savePref(PUBLIC_KEY, next ? 'on' : 'off'); };

  // Per-mode source sets (§5.3). Elections always includes public political
  // events; the toggle only exists in Session mode.
  const sources = useMemo(() => {
    if (mode === 'elections') return ['election', 'public', 'workspace'];
    return showPublic ? ['legislative', 'workspace', 'public'] : ['legislative', 'workspace'];
  }, [mode, showPublic]);
  const stripRef = useRef<HTMLDivElement>(null);
  // Track-into-workspace picker (PRD §5.4b); legislative + public events only
  const [trackingEvent, setTrackingEvent] = useState<{ key: string; title: string } | null>(null);
  const [detailsEvent, setDetailsEvent] = useState<CalendarEvent | null>(null);
  const dayRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const { data, isLoading } = useMyCalendarEvents(anchor, sources, myOnly);
  const events = useMemo(() => data?.events ?? [], [data]);

  // Smart default (§5.7): with no stored choice we fetched All Events (myOnly
  // false); once the window arrives, users with anything of theirs in it flip
  // to My Events. Runs once per mount; a stored or clicked choice wins.
  React.useEffect(() => {
    if (scopeChoice !== null || derivedScope !== null || !data) return;
    setDerivedScope(deriveDefaultScope(data.events ?? []));
  }, [scopeChoice, derivedScope, data]);
  const total = data?.total ?? events.length;

  const windowDays = useMemo(
    () =>
      Array.from({ length: PRO_HOME_CALENDAR_DAYS }, (_, i) => {
        const date = addDays(parseISO(anchor), i);
        return { key: format(date, 'yyyy-MM-dd'), date };
      }),
    [anchor],
  );

  const eventsByDay = useMemo(() => {
    const grouped = new Map<string, CalendarEvent[]>();
    for (const event of events) {
      const day = toDateOnly(event.reading_date);
      const list = grouped.get(day) ?? [];
      list.push(event);
      grouped.set(day, list);
    }
    return new Map([...grouped.entries()].sort(([a], [b]) => a.localeCompare(b)));
  }, [events]);

  const eventDays = useMemo(() => new Set(eventsByDay.keys()), [eventsByDay]);

  const scrollToDay = (day: string) => {
    dayRefs.current[day]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const shiftWindow = (days: number) => {
    setAnchor(format(addDays(parseISO(anchor), days), 'yyyy-MM-dd'));
    stripRef.current?.scrollTo({ left: 0 });
  };

  const selectDate = (dateStr: string) => {
    setAnchor(dateStr);
    stripRef.current?.scrollTo({ left: 0 });
  };

  const anchorIsToday = anchor === todayKey;

  // "Open full calendar" lands on the same day this panel is showing.
  const anchorDate = parseISO(anchor);
  // Mode-matched full calendar (§5.3): Session → Legislative, Elections →
  // Election Calendar.
  const fullCalendarBase = mode === 'elections' ? '/elections/calendar' : '/calendar';
  const fullCalendarUrl = `${fullCalendarBase}?year=${format(anchorDate, 'yyyy')}&month=${format(
    anchorDate,
    'M',
  )}&day=${format(anchorDate, 'd')}`;

  return (
    <section
      className="card p-0 flex flex-col overflow-hidden min-h-0 max-h-[520px] xl:max-h-none"
      aria-label="My Calendar"
      data-testid="dashboard-calendar"
    >
      {/* Header: title + date-picker popover + scrollable day strip */}
      <div className="flex-none border-b border-border-light dark:border-border-dark-hairline px-4 pt-3.5 pb-3 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 font-display font-semibold text-[15px] text-primary-800 dark:text-text-dark-primary">
            <DatePickerPopover
              selectedDate={anchor}
              onSelectDate={selectDate}
              hasEvent={(dateStr) => eventDays.has(dateStr)}
            />
            My Calendar
          </h2>
          <div className="flex items-center gap-1.5">
            {/* Session/Elections comes from the page-level mode toggle. */}
            {/* My / All toggle */}
            <div className="flex rounded-md border border-border-light dark:border-border-dark-hairline overflow-hidden" role="group" aria-label="Event scope">
              {([true, false] as const).map((isMy) => (
                <button
                  key={isMy ? 'my' : 'all'}
                  type="button"
                  aria-pressed={myOnly === isMy}
                  onClick={() => setMyOnly(isMy)}
                  className={`px-2 py-0.5 text-[11px] font-semibold transition-colors ${
                    myOnly === isMy
                      ? 'bg-primary-800 text-white'
                      : 'bg-white dark:bg-background-dark-secondary text-slate-500 dark:text-text-dark-secondary hover:text-primary-700'
                  }`}
                >
                  {isMy ? 'My Events' : 'All Events'}
                </button>
              ))}
            </div>
            {/* Public events are optional in Session mode only — Elections
                always includes them (§5.3, resolved O1) */}
            {mode === 'session' && (
            <button
              type="button"
              aria-pressed={showPublic}
              onClick={() => setShowPublic(!showPublic)}
              title={showPublic ? 'Hide public events' : 'Show public events'}
              className={`rounded-md border px-2 py-0.5 text-[11px] font-semibold transition-colors ${
                showPublic
                  ? 'bg-primary-tint dark:bg-primary-900/20 border-primary-tint-border dark:border-primary-800 text-primary-700 dark:text-primary-300'
                  : 'bg-white dark:bg-background-dark-secondary border-border-light dark:border-border-dark-hairline text-slate-400 dark:text-text-dark-tertiary'
              }`}
            >
              Public
            </button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => shiftWindow(-7)}
            aria-label="Previous week"
            className="flex-none rounded-md p-0.5 text-slate-400 hover:text-primary-600 hover:bg-neutral-100 dark:hover:bg-background-dark-tertiary"
          >
            <ChevronLeft className="w-4 h-4" aria-hidden="true" />
          </button>
          <div ref={stripRef} className="pro-home-strip flex-1 min-w-0 flex gap-1.5">
            {windowDays.map(({ key, date }) => {
              const dayEvents = eventsByDay.get(key) ?? [];
              const isTodayCell = isToday(date);
              const hasEvents = dayEvents.length > 0;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => scrollToDay(key)}
                  className={`flex-none w-12 rounded-[10px] border py-1.5 flex flex-col items-center gap-0.5 transition-colors ${
                    isTodayCell
                      ? 'bg-primary-800 border-primary-800 text-white'
                      : hasEvents
                        ? 'bg-white dark:bg-background-dark-secondary border-border-light dark:border-border-dark-hairline text-primary-800 dark:text-text-dark-primary hover:border-primary-300'
                        : 'bg-neutral-100 dark:bg-background-dark-tertiary border-border-light dark:border-border-dark-hairline text-slate-400 dark:text-text-dark-tertiary'
                  }`}
                  aria-label={`${format(date, 'EEEE, MMMM d')} — ${dayEvents.length} events`}
                >
                  <span className="text-[9.5px] font-semibold uppercase">{format(date, 'EEE')}</span>
                  <span className="font-display font-bold text-[15px] tabular-nums leading-none">
                    {format(date, 'd')}
                  </span>
                  <span className="flex gap-0.5 h-1" aria-hidden="true">
                    {dayEvents.slice(0, 4).map((_, dot) => (
                      <span
                        key={dot}
                        className={`w-1 h-1 rounded-full ${isTodayCell ? 'bg-white/80' : 'bg-primary-300'}`}
                      />
                    ))}
                  </span>
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => shiftWindow(7)}
            aria-label="Next week"
            className="flex-none rounded-md p-0.5 text-slate-400 hover:text-primary-600 hover:bg-neutral-100 dark:hover:bg-background-dark-tertiary"
          >
            <ChevronRight className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Agenda */}
      <div className="pro-home-scroll flex-1 min-h-0">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <SpinnerIcon className="w-5 h-5 animate-spin text-primary-400" aria-hidden="true" />
          </div>
        ) : eventsByDay.size === 0 ? (
          <p className="px-4 py-10 text-sm text-slate-500 dark:text-text-dark-secondary text-center">
            {anchorIsToday
              ? 'No events in the next two weeks.'
              : `No events in the two weeks from ${format(parseISO(anchor), 'MMMM d')}.`}
          </p>
        ) : (
          [...eventsByDay.entries()].map(([day, dayEvents]) => (
            <div
              key={day}
              ref={(el) => {
                dayRefs.current[day] = el;
              }}
            >
              <div className="sticky top-0 z-10 bg-neutral-100 dark:bg-background-dark-tertiary border-b border-border-light dark:border-border-dark-hairline px-4 py-1.5 text-[10px] font-bold uppercase tracking-[.12em] text-primary-500 dark:text-primary-dark-500">
                {formatAgendaDay(day)}
              </div>
              {dayEvents.map((event) => {
                const tags = workspaceTags(event);
                return (
                  <div
                    key={event.event_key ?? event.calendar_id}
                    className="grid grid-cols-[64px_1fr] gap-2.5 px-4 py-2.5 border-b border-border-light dark:border-border-dark-hairline"
                  >
                    <span className="text-xs font-bold tabular-nums text-text-primary dark:text-text-dark-primary pt-0.5">
                      {event.all_day ? 'All day' : formatEventTime(event)}
                    </span>
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 text-[12.5px] font-semibold text-primary-800 dark:text-text-dark-primary">
                        <button
                          type="button"
                          onClick={() => setDetailsEvent(event)}
                          className="truncate text-left hover:underline"
                        >
                          {event.reading_title}
                        </button>
                        {event.event_source && event.event_source !== 'legislative' && (
                          <EventSourceBadge source={event.event_source} workspaceName={event.workspace_name} />
                        )}
                        {(event.event_source === 'public' || event.event_source === 'legislative') && event.event_key && (
                          <button
                            type="button"
                            onClick={() =>
                              setTrackingEvent({ key: event.event_key as string, title: event.reading_title || '' })
                            }
                            aria-label={
                              (event as any).is_tracked || (event as any).is_tracked_event
                                ? 'Tracked — edit workspaces'
                                : 'Track this event'
                            }
                            title="Track in workspaces"
                            className="flex-none rounded p-0.5 text-slate-300 hover:text-primary-600 dark:text-text-dark-tertiary"
                          >
                            <Bookmark
                              className={`w-3.5 h-3.5 ${
                                (event as any).is_tracked || (event as any).is_tracked_event
                                  ? 'fill-primary-500 text-primary-500'
                                  : ''
                              }`}
                              aria-hidden="true"
                            />
                          </button>
                        )}
                      </div>
                      {event.attribution && (
                        <div className="mt-0.5"><EventAttribution attribution={event.attribution} /></div>
                      )}
                      <div className="flex items-center gap-2 mt-0.5 min-w-0">
                        <span className="text-[11px] text-slate-400 dark:text-text-dark-tertiary truncate">
                          {event.derived_location || event.room_number || event.chamber}
                        </span>
                        {tags.map((name) => (
                          <span
                            key={name}
                            className="inline-flex items-center gap-1 text-[11px] text-slate-500 dark:text-text-dark-secondary flex-none"
                          >
                            <span
                              className={`w-1.5 h-1.5 rounded-[2px] ${workspaceDotClass(name)}`}
                              aria-hidden="true"
                            />
                            {name}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>

      {/* Footer: the honest total */}
      <div className="flex-none border-t border-border-light dark:border-border-dark-hairline bg-neutral-50 dark:bg-background-dark-tertiary px-4 py-2.5 flex items-center justify-between">
        <span className="text-[11.5px] text-slate-500 dark:text-text-dark-secondary">
          {total} event{total === 1 ? '' : 's'}{' '}
          {anchorIsToday
            ? `in the next ${PRO_HOME_CALENDAR_DAYS} days`
            : `in the ${PRO_HOME_CALENDAR_DAYS} days from ${format(parseISO(anchor), 'MMM d')}`}
        </span>
        <Link
          to={fullCalendarUrl}
          className="text-xs font-semibold text-primary-600 dark:text-primary-dark-500 hover:text-primary-700"
        >
          Open full calendar →
        </Link>
      </div>
      {detailsEvent && (
        <EventDetailsModal event={detailsEvent} isOpen onClose={() => setDetailsEvent(null)} />
      )}
      {trackingEvent && (
        <EventWorkspaceSelectionModal
          eventKey={trackingEvent.key}
          eventTitle={trackingEvent.title}
          isOpen
          onClose={() => setTrackingEvent(null)}
        />
      )}
    </section>
  );
};

export default MyCalendarPanel;
